import React from 'react';
import { logger, scrollToTop } from '@utils/usefulFuncs';
import { Body5, Container, H5, Page } from '@ui';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import styled from 'styled-components';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { SelectedDocuments } from '@containers/DigitalAnalyst/components/SelectedDocuments';
import { TextSearchResults } from '@containers/DigitalAnalyst/components/TextSearchResults';
import ErrorText from '@ui/ErrorText';
import { DocumentProvider } from '../contexts/DocumentProvider';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { useNavigate } from 'react-router';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS, DOCUMENT_SELECTION_MODES } from '@constants';
import { DocumentSelectionModalControlled } from '@features/document/DocumentSelectionModal/DocumentSelectionModalControlled';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;
const SearchTermInputWrapper = styled.div`
  padding: 8px 0 0 0;

  display: grid;
  grid-template-columns: 1fr 108px; /* Adjust sizes as needed */

  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    height: 40px;

    padding: 9px 16px;
    resize: none;
  }

  button {
    margin-left: 24px;
    padding: 11px 14px;
  }
`;

const tool = AI_TOOLS.TEXT_SEARCH;

const docSelectModes = [
  DOCUMENT_SELECTION_MODES.fedDoc,
  DOCUMENT_SELECTION_MODES.speaker,
  DOCUMENT_SELECTION_MODES.docType,
];

const SearchTermInput = ({ index, savedValue, onSearchTermEnter, onDeleteTerm }) => {
  const ref = React.useRef(null);
  const [value, setValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    setValue(savedValue);
  }, [savedValue]);

  React.useEffect(() => {
    function handleKeyPress(event) {
      if (!isFocused) {
        return;
      }
      if (event.key === 'Enter') {
        onEnter();
      }
    }

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFocused, value]);

  const hc = (e) => setValue(e.target.value);

  const hf = () => setIsFocused(true);
  const hb = () => {
    setIsFocused(false);
    onEnter();
  };

  const onDelete = () => {
    onDeleteTerm(index);
  };
  const onEnter = () => {
    if (value === '') {
      return;
    }

    onSearchTermEnter(value, index);
    if (index === null) {
      setValue('');
      ref?.current?.focus();
    }
  };

  return (
    <SearchTermInputWrapper>
      <input ref={ref} value={value} onChange={hc} onFocus={hf} onBlur={hb} />
      {isFocused && <FlatButton onClick={onEnter}>+ Enter</FlatButton>}
      {!isFocused && index !== null && <TransparentFlatButton onClick={onDelete}>Delete</TransparentFlatButton>}
    </SearchTermInputWrapper>
  );
};

export function TextSearch() {
  const navigate = useNavigate();

  const [searchTerms, setSearchTerms] = React.useState([]);
  const [selectedDocs, setSelectedDocs] = React.useState([]);

  const [submittedSearchTerms, setSubmittedSearchTerms] = React.useState(null);
  const [submittedDocs, setSubmittedDocs] = React.useState([]);

  const [error, setError] = React.useState(null);

  const [isSelectingDocs, setIsSelectingDocs] = React.useState(false);
  const [defaultActiveDocSelectMode] = React.useState(DOCUMENT_SELECTION_MODES.fedDoc);

  const onGoBack = () => navigate(ROUTES.home);

  const onDismiss = () => setIsSelectingDocs(false);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  const onSubmit = async () => {
    if (searchTerms.length === 0) {
      setError('Please enter search terms');
      return;
    }
    if (selectedDocs.length === 0) {
      setError('Please select at least one document to search');
      return;
    }

    setSubmittedSearchTerms(null);
    setError(null);
    setTimeout(() => {
      setSubmittedSearchTerms(searchTerms.filter((t) => t !== ''));
    }, 100);
    setSubmittedDocs(selectedDocs);
    scrollToResults();
  };
  const onClear = async () => {
    scrollToTop(100);
    setSearchTerms([]);
    setSelectedDocs([]);
    setError(null);
    setSubmittedSearchTerms(null);
    setSubmittedDocs([]);
  };

  const scrollToResults = () => {
    try {
      const delay = 300;
      const id = 'text-search-results';
      const yOffset = -26;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, delay);
      }
    } catch (e) {
      logger('error', e);
    }
  };
  const onSelectedDocsChange = (t) => setSelectedDocs(t);

  const onSelectDocumentsClick = () => setIsSelectingDocs(true);

  const onSearchTermEnter = (v, index) => {
    if (v === '') return;

    if (index === null) {
      setSearchTerms([...searchTerms, v]);
    } else {
      const nextTerms = [...searchTerms];
      nextTerms[index] = v;
      setSearchTerms(nextTerms);
    }
  };
  const onDeleteTerm = (index) => {
    const nextTerms = [...searchTerms];
    setSearchTerms(nextTerms.filter((t, i) => i !== index));
  };

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page>
          <Container>
            <BackArrowButton onClick={onGoBack} />
            <Container top={36}>
              <H5>Fed Document Text Search</H5>
            </Container>
            {isSelectingDocs ? (
              <DocumentSelectionModalControlled
                documentSelectionModes={docSelectModes}
                defaultActiveMode={defaultActiveDocSelectMode}
                onDismiss={onDismiss}
                selectedDocs={selectedDocs}
                onSelectedDocsChange={onSelectedDocsChange}
                tool={tool}
              />
            ) : null}
            <Container top={24}>
              <Body5 bold>Enter Search Terms:</Body5>
            </Container>
            <Container top={8}>
              {searchTerms.map((s, i) => (
                <SearchTermInput
                  key={i}
                  index={i}
                  savedValue={s}
                  onSearchTermEnter={onSearchTermEnter}
                  onDeleteTerm={onDeleteTerm}
                />
              ))}
            </Container>
            <Container>
              <SearchTermInput
                index={null}
                savedValue={''}
                onSearchTermEnter={onSearchTermEnter}
                onDeleteTerm={onDeleteTerm}
              />
            </Container>

            <Container top={16}>
              <SelectedDocuments
                onSelectDocumentsClick={onSelectDocumentsClick}
                selectedDocs={selectedDocs}
                onSelectedDocsChange={onSelectedDocsChange}
                showAddAllDocumentsAction={false}
              />
            </Container>

            <Container top={48}>
              {error && (
                <Container bottom={8} centerAll>
                  <ErrorText>{error}</ErrorText>
                </Container>
              )}
              <Container style={{ textAlign: 'center' }}>
                <FlatButton onClick={onSubmit} fullWidth>
                  Submit
                </FlatButton>
              </Container>
              <Container top={4} style={{ textAlign: 'center' }}>
                <TransparentFlatButton onClick={onClear} fullWidth>
                  Clear
                </TransparentFlatButton>
              </Container>
            </Container>
          </Container>
        </Page>
        <a id={'text-search-results'} />
        <DocumentProvider documents={submittedDocs}>
          {submittedSearchTerms !== null &&
            submittedSearchTerms.map((t, i) => (
              <Page key={i + t} noAutoScroll>
                <TextSearchResults selectedDocs={selectedDocs} searchTerm={t} index={i} />
              </Page>
            ))}
        </DocumentProvider>
      </Body>
    </PageWrapper>
  );
}
